import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeNavLook = 'nav-ctrl' | 'nav-ring-elite' | 'nav-ring-gold' | 'nav-ring-accent' | 'nav-account' | 'nav-account-collapsed' | 'nav-signout' | 'nav-signout-collapsed';
const NAV_LOOKS = new Set<string>(['nav-ctrl', 'nav-ring-elite', 'nav-ring-gold', 'nav-ring-accent', 'nav-account', 'nav-account-collapsed', 'nav-signout', 'nav-signout-collapsed']);
export const isWedgeNavLook = (look: string): look is WedgeNavLook => NAV_LOOKS.has(look);

/* No nav look draws a transparent target: each control is 44px already and the bar spaces them 8px apart. */
export const wedgeNavDrawsHit = (look: string): boolean => !look.startsWith('nav-');

/* Released controls of the phone top bar. The back control is a 44px round disc on the surface fill with a half pixel
   hairline in the separator role, label ink and no padding. A ring control is the same 44px disc with a 1.5px border in its
   tone, a 9% fill of that tone, tone ink, and the custom property --rv-ring that the shipped pulse and hover sheet read.
   Tones: elite (calendar), gold (camera), accent (bell). Rings set the shipped transitions and touch action.
   The side nav account block is a full width row (12px radius, 10px padding, 11px gap, left aligned) with no fill of its own, so the
   shipped .wedge-sidenav-item hover sheet still paints it; collapsed, it centres one avatar with 10px by 0 padding. The Sign out row is a
   10px radius full width row in the red ink at 14px weight 500 with transparent fill (12px gap and 10px 12px padding, or none and 10px
   8px collapsed) and the three shipped transitions. */
const TONE = {'nav-ring-elite': 'tierElite', 'nav-ring-gold': 'gold', 'nav-ring-accent': 'accent'} as const;

export function wedgeNavStyle(look: WedgeNavLook): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'nav-ctrl') return {
    width: 44, height: 44, borderRadius: 999,
    background: r('surface'), border: `0.5px solid ${r('separator')}`,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', color: r('label'), flexShrink: 0,
    fontFamily: r('font'), padding: 0,
  };
  if (look === 'nav-account') return {
    display: 'flex', alignItems: 'center', gap: 11,
    width: '100%', textAlign: 'left',
    padding: '10px 10px', borderRadius: 12,
    border: 0, cursor: 'pointer',
    fontFamily: r('font'),
  };
  if (look === 'nav-account-collapsed') return {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    width: '100%', padding: '10px 0', borderRadius: 12,
    border: 0, cursor: 'pointer',
  };
  if (look === 'nav-signout' || look === 'nav-signout-collapsed') {
    const collapsed = look === 'nav-signout-collapsed';
    return {
      display: 'flex', alignItems: 'center',
      gap: collapsed ? 0 : 12,
      justifyContent: collapsed ? 'center' : 'flex-start',
      width: '100%', textAlign: 'left',
      padding: collapsed ? '10px 8px' : '10px 12px',
      borderRadius: 10, border: 0,
      background: 'transparent',
      color: r('red'),
      cursor: 'pointer',
      fontFamily: r('font'), fontSize: 14, fontWeight: 500,
      transition: `background 140ms ${r('ease')}, padding 160ms ${r('ease')}, gap 160ms ${r('ease')}`,
    };
  }
  const tone = r(TONE[look]);
  return {
    ['--rv-ring' as string]: tone,
    position: 'relative',
    width: 44, height: 44, borderRadius: 999, flexShrink: 0, padding: 0,
    border: `1.5px solid ${tone}`,
    background: `color-mix(in srgb, ${tone} 9%, transparent)`,
    color: tone,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', fontFamily: r('font'),
    transition: `background 160ms ${r('ease')}, transform 120ms ${r('ease')}`,
    WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
  };
}
